import { ScrollView, Text, View } from 'react-native';
import { BrandPanel } from '~/components/BrandPanel';
import { useAuth } from '~/hooks/useAuth';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Frame 00c — signed in, no operator access.
 *
 * The entry shell with one message and one way out. It is reached when the API answers
 * `ERR_NO_ROLE`: Microsoft signed this person in, and our service holds no
 * `Glass.Operator` role for them, so every screen behind here would refuse them.
 *
 * It replaces the operator home rather than sitting on top of it. Before this, a person
 * without the role reached Recent journeys in full — heading, New inspection button and
 * all — with the refusal as a notice above it, and the button 403'd on every press. The
 * e2e scenario asserted the button was absent and passed only because it looked before
 * the screen had drawn.
 *
 * Sign out is the ONLY action, which is the frame's own design and is also the only thing
 * that can help: the account is wrong, so the fix is a different account or an
 * administrator, and there is nothing here to retry.
 */
export function NoAccessScreen() {
  const auth = useAuth();
  const { c, t, space } = useTheme();

  // Only ever rendered for `no_access`, which carries the account; the fallback is so this
  // cannot crash if it is ever mounted for another status.
  const who = auth.status === 'no_access' ? auth.user.upn : '';

  return (
    <View style={{ flex: 1, backgroundColor: c.surface }}>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} bounces={false}>
        <BrandPanel />
        <View
          style={{
            flex: 1,
            paddingHorizontal: space.lg,
            paddingTop: space.xl,
            justifyContent: 'space-between',
          }}
        >
          <View style={{ gap: space.lg, paddingBottom: space.xl }}>
            <View style={{ gap: space.sm }}>
              <Text testID="no-access-title" style={[t.h2, { color: c.text }]}>
                No operator access
              </Text>
              {/*
                The frame says "add you to glass-operators". D20 grants operator access as
                an Entra APP ROLE, not a group, so the prototype's name is for something an
                administrator cannot grant — and the whole value of this sentence is that
                the person can repeat it accurately to someone who can act on it. Named
                here in our own copy, never read back from the API: the service refuses to
                say which role is missing, so that a caller cannot enumerate roles by
                probing, and this screen is not a way around that.
              */}
              <Text testID="no-access-detail" style={[t.body, { color: c.dim, lineHeight: 22 }]}>
                You’re signed in, but your account doesn’t have operator access. Ask an
                administrator to assign you the{' '}
                <Text style={{ color: c.text, fontWeight: '600' }}>Glass.Operator</Text> role.
              </Text>
            </View>

            {/*
              Whose account this is. On a shared handset it is the first thing to check.

              `monoValue`, not the frame's 14/24 mono: the scale has no 14px mono step and
              this is the only place that would want one. Recorded as a deviation rather
              than adding a token with one caller.
            */}
            <Text testID="no-access-account" style={[t.monoValue, { color: c.dim }]}>
              {who}
            </Text>

            {/*
              Underlined, like the support link on 00. The design system defines no link
              colour, so the underline is the only affordance this has — and it is the one
              signal that survives both the greyscale and the glare check.
            */}
            <Text
              testID="no-access-sign-out"
              accessibilityRole="button"
              onPress={() => void auth.signOut()}
              style={[
                t.bodyLg,
                { color: c.text, fontWeight: '600', textDecorationLine: 'underline' },
              ]}
            >
              Sign out
            </Text>
          </View>

          <View style={{ paddingBottom: space.md }}>
            <Text style={[t.sub, { color: c.dim }]}>© scans.ai · Internal use only</Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
